import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import {
  Flex,
  Input,
  Popover,
  Select,
  SelectProps,
  Spin,
  Tag,
  Tooltip
} from "antd";
import { useTranslation } from "react-i18next";

import { User } from "@/types/user";
import { userFetchs } from "@/apis/user";
import { debounce } from "lodash";
import { getUserAvatarTip, getUserName } from "@/utils/user";
import WMSAvatar from "@/styles/WMSAvatar";
import { PAGE_SIZE } from "@/utils/const";

interface SSelectUsersProps extends Omit<SelectProps, 'options'> {
  value?: string[];
  onChange?: (value: string[]) => void;
  defaultList?: Array<User>;
  width?: string;
  getList?: (list: Array<User>, val: string[]) => void;
  disabledUsers?: string[];
  getSelUser?: (user: User) => void;
}

const SelectUsers = ({
  value,
  onChange,
  defaultList,
  width,
  getList,
  disabledUsers = [],
  getSelUser,
  ...props
}: SSelectUsersProps) => {
  const { t } = useTranslation();

  const [list, setList] = useState<Array<User>>(
    defaultList ? defaultList : []
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(1);
  const [listLoading, setListLoading] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const fetchingRef = useRef<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (defaultList && defaultList.length > 0 && getList)
      getList(defaultList, defaultList.map(u => u.id));
  }, [defaultList]);

  const fetch = useCallback((keyword: string, current: number) => {
    // 取消之前的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // 创建新的 AbortController
    abortControllerRef.current = new AbortController();

    // 避免在已有请求时设置 loading，减少闪烁
    if (!fetchingRef.current) {
      setListLoading(true);
    }
    fetchingRef.current = true;

    userFetchs({
      body: keyword,
      pagination: { offset: (current - 1) * PAGE_SIZE, take: PAGE_SIZE }
    })
      .then(res => {
        if (current === 1) {
          const info = defaultList ? defaultList : [];
          const restList = res.body.filter(
            item => !info.map(dI => dI.id)?.includes(item.id)
          );
          setList([...info, ...restList]);
        } else {
          setList(prevList => [...prevList, ...res.body]);
        }
        setTotal(res.pagination?.size ?? 0);
      })
      .catch(err => {
        // 忽略取消请求的错误
        if (err.name !== 'AbortError') {
          console.error('Fetch users error:', err);
        }
      })
      .finally(() => {
        fetchingRef.current = false;
        setListLoading(false);
      });
  }, [defaultList]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    if (
      !fetchingRef.current &&
      currentPage * PAGE_SIZE < total &&
      target.scrollTop + target.offsetHeight >= target.scrollHeight
    ) {
      let page = currentPage + 1;
      fetch(keyword, page);
      setCurrentPage(page);
    }
  };

  const onSearch = useRef(
    debounce((val: string) => {
      setKeyword(val.trim());
      setCurrentPage(1);
      fetch(val.trim(), 1);
    }, 300)
  ).current;

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      onSearch.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [onSearch]);

  const optionRender = (user: User): ReactNode => {
    return (
      <Flex gap={4} align="center" style={{ width: "100%" }}>
        <WMSAvatar
          uniChar={user.id}
          creator={user}
          alt={getUserAvatarTip(user)}
          avatarId={user.avatar}
          size="small"
        />
        <span style={{ flex: 1 }}>{getUserName(user)}</span>
      </Flex>
    );
  };

  const tagRender = (tagProps: {
    label: React.ReactNode;
    value: any;
    disabled: boolean;
    onClose: (event?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    closable: boolean;
    isMaxTag: boolean;
  }) => {
    const { label, value, onClose, closable } = tagProps;
    const user = list.find(u => u.id === value);
    if (user) {
      let isDisUser = disabledUsers?.find(s => s === user?.id);
      return (
        <Tag
          closable={closable && !!!isDisUser}
          onClose={onClose}
          style={{ marginInlineEnd: 4 }}
          closeIcon={<span style={{ fontSize: 10 }}>&times;</span>}
        >
          <WMSAvatar
            uniChar={user.id}
            alt={getUserAvatarTip(user)}
            creator={user}
            avatarId={user.avatar}
            size={20}
            style={{ fontSize: 10 }}
          />
          <span>{getUserName(user)}</span>
        </Tag>
      );
    }
    return (
      <Tag
        closable
        onClose={onClose}
        style={{ marginInlineEnd: 4 }}
      >
        {label}
      </Tag>
    );
  };

  return (
    <Tooltip title={value?.length ? "" : t("reimburse.plSelectUsers")}>
      <Select
        mode="multiple"
        value={value}
        filterOption={false}
        onDropdownVisibleChange={open => {
          if (open) {
            setKeyword("");
            setSearchInput("");
            setCurrentPage(1);
            fetch("", 1);
          }
        }}
        onSelect={(val, option) => {
          const newValue = [...(value || []), val];
          onChange && onChange(newValue);
          getList && getList(list, newValue);
          getSelUser && getSelUser(option.user);
        }}
        onDeselect={(val) => {
          const newValue = value?.filter(v => v !== val) || [];
          onChange && onChange(newValue);
          getList && getList(list, newValue);
        }}
        style={{ width: width ? width : "100%" }}
        placeholder={t("reimburse.plSelectUsers")}
        options={list.filter(item => !disabledUsers.includes(item.id)).map(item => ({
          key: item.id,
          value: item.id,
          label: getUserName(item),
          disabled: (disabledUsers || []).includes(item.id),
          user: item
        }))}
        optionRender={(info) => optionRender(info.data.user)}
        tagRender={tagRender}
        onPopupScroll={handleScroll}
        dropdownRender={ele => (
          <>
            <div style={{ padding: "8px" }}>
              <Input.Search
                placeholder={t("category.plSearch")}
                value={searchInput}
                onChange={e => {
                  setSearchInput(e.target.value);
                  onSearch(e.target.value);
                }}
                onSearch={val => {
                  onSearch(val);
                }}
                style={{ width: "100%" }}
              />
            </div>
            {ele}
            {listLoading && (
              <Flex
                style={{ width: "100%", padding: 10 }}
                justify="center"
                align="center"
              >
                <Spin />
              </Flex>
            )}
          </>
        )}
        {...props}
      />
    </Tooltip>
  );
};

export default SelectUsers;
